import React from 'react';
import Tooltip from 'cccisd-tooltip';
import Modal from 'cccisd-modal';
import Table from 'cccisd-table';
import Loader from 'cccisd-loader';
import { format, max } from 'date-fns';
import { Link } from 'cccisd-react-router';
import IconArrow from 'cccisd-icons/square-up-right';
import IconModal from 'cccisd-icons/popout';
import Tabs from 'cccisd-tabs';
import { Progress } from 'cccisd-laravel-assignment';
import usePdProjectId from '../usePdProjectId.js';
import style from './style.css';

export const learners = ({ learnerList, context, adjustColumns }) => {
    const columns = [
        {
            name: 'studentId',
            label: 'Student ID',
            sort: true,
            filter: true,
        },
        {
            name: 'fields',
            label: 'Student Name',
            sort: true,
            filter: true,
            render: v => `${v.firstName} ${v.lastName}`,
        },
        {
            name: 'ancestorGroups',
            label: 'School',
            sort: true,
            filter: true,
            render: v => v.site.group.label,
            filterSettings: {
                type: 'text',
                custom: (r, value) => {
                    return r.ancestorGroups.site.group.label?.toLowerCase().includes(value.toLowerCase());
                },
            },
        },
        {
            name: 'parentRoles',
            label: 'Provider',
            sort: true,
            filter: true,
            render: v => v.instructor.user.fullNameWithUsername || '',
            filterSettings: {
                type: 'text',
                custom: (r, value) => {
                    return r.parentRoles.instructor.user.fullNameWithUsername
                        ?.toLowerCase()
                        .includes(value.toLowerCase());
                },
            },
            sortSettings: {
                custom: row => {
                    return row.parentRoles.instructor.user.fullNameWithUsername?.toLowerCase();
                },
            },
        },
        {
            name: 'selectedAssignmentPlanList',
            label: 'Active Plans',
            sort: true,
            render: v => {
                if (v.length < 1) {
                    return 0;
                }
                const activePlans = v.filter(plan => plan.openSessions.length > 0);
                return assessmentPlans({
                    planList: activePlans,
                    context: v[0].selectedByPawn?.fields.studentId
                        ? `Student ID: ${v[0].selectedByPawn.fields.studentId}`
                        : '',
                    adjustColumns: c => {
                        return c.filter(col => col.name !== 'selectedByPawnList' && col.label !== 'Actions');
                    },
                    row: learnerList,
                });
            },
            renderCSV: value => value.filter(plan => plan.openSessions.length > 0).length,
        },
    ];
    return (
        <div>
            {learnerList.length > 0 ? (
                <Tooltip title={`Students ${context}`}>
                    <Modal
                        trigger={
                            <a href="#">
                                <IconModal /> {learnerList.length}
                            </a>
                        }
                        title={`Students ${context}`}
                        size="large"
                    >
                        <div style={{ textAlign: 'center' }}>
                            <Table
                                data={learnerList}
                                columns={adjustColumns ? adjustColumns(columns) : columns}
                                isCsvDownload
                            />
                        </div>
                    </Modal>
                </Tooltip>
            ) : (
                0
            )}
        </div>
    );
};

export const assessmentPlans = ({ planList, context, adjustColumns, row }) => {
    let plans = [...planList];
    plans = plans.map(plan => {
        const closeDates = [];
        const surveys = new Set();

        plan.sessionList.forEach(sesh => {
            closeDates.push(new Date(sesh.closesDate));

            sesh?.settings?.sessionCollection?.map(sc => surveys.add(sc.label));
        });

        const latestDate = max(closeDates);

        return {
            ...plan,
            maxCloseDate: latestDate !== 'Invalid Date' ? format(latestDate, 'MM/dd/yyyy') : '',
            surveys: [...surveys],
        };
    });

    const columns = [
        { name: 'label', label: 'Plan Name', sort: true, filter: true },
        {
            name: 'description',
            label: 'Description',
            sort: true,
            filter: true,
            render: v => {
                return (
                    <Tooltip title={v}>
                        <div className={style.lineClamp}>{v}</div>
                    </Tooltip>
                );
            },
            renderCSV: value => value,
        },
        {
            name: 'createdDate',
            label: 'Created Date',
            sort: true,
            filter: true,
            render: v => {
                return format(new Date(v), 'MM/dd/yyyy');
            },
            filterSettings: {
                type: 'date',
            },
        },
        {
            name: 'assignmentPlanId',
            label: 'Surveys',
            filter: true,
            render: v => {
                const surveyContent = plans
                    .find(plan => plan.assignmentPlanId === v)
                    .surveys.map(survey => {
                        return <li key={survey}>{survey}</li>;
                    });
                return (
                    <Tooltip title={surveyContent}>
                        <div className={style.lineClamp}>{surveyContent}</div>
                    </Tooltip>
                );
            },
            renderCSV: value => {
                return plans.find(plan => plan.assignmentPlanId === value)?.surveys.join(', ');
            },
            filterSettings: {
                type: 'text',
                custom: (r, value) => {
                    return r.surveys.join(' ').toLowerCase().includes(value.toLowerCase());
                },
            },
        },
        { name: 'maxCloseDate', label: 'Closes On', sort: true, filter: true, render: v => (v ? v : '') },
        {
            name: 'createdByPawn',
            label: 'Provider',
            sort: true,
            filter: true,
            render: v => v.user.fullNameWithUsername || '',
            filterSettings: {
                type: 'text',
                custom: (r, value) => {
                    return r.createdByPawn.user.fullNameWithUsername.toLowerCase().includes(value.toLowerCase());
                },
            },
            sortSettings: {
                custom: r => {
                    return r.createdByPawn.user.fullNameWithUsername.toLowerCase();
                },
            },
        },
        {
            name: 'selectedByPawnList',
            label: 'Students in plan',
            sort: true,
            render: v => {
                return v && v.length;
            },
        },
        {
            name: 'actions',
            label: 'Actions',
            render: v => {
                return (
                    <div>
                        <Link
                            to={{
                                pathname: '/results/:assignmentPlanId/:pawnId',
                                state: {
                                    provider: row ? row['pawn.pawnId'] : '',
                                    assignmentPlanId: row.assignmentPlanId,
                                    learnerId: row ? row['fields.studentId'] : '',
                                },
                            }}
                        >
                            <IconArrow /> View on Results Page
                        </Link>
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            {plans.length > 0 ? (
                <Tooltip title={`Assessment Plans ${context && `(${context})`}`}>
                    <Modal
                        trigger={
                            <a href="#">
                                <IconModal /> {plans.length}
                            </a>
                        }
                        title={`Assessment Plans ${context && `(${context})`}`}
                        size="99vw"
                    >
                        <div style={{ textAlign: 'center' }}>
                            <Table
                                data={plans}
                                columns={adjustColumns ? adjustColumns(columns) : columns}
                                isCsvDownload
                                csvOmittedColumns={['actions']}
                            />
                        </div>
                    </Modal>
                </Tooltip>
            ) : (
                0
            )}
        </div>
    );
};

const ProgressWithProjectId = props => {
    const [projectId, loadingProjectId] = usePdProjectId();

    if (loadingProjectId) {
        return <Loader loading />;
    }

    return <Progress projectId={projectId} {...props} />;
};

export const renderCourseProgress = (list, roleHandles) => {
    const pawnInfo = list.reduce((obj, item) => {
        obj[item.id] = { name: item.name, email: item.email };
        return obj;
    }, {});
    const pawnIds = Object.keys(pawnInfo).map(Number);
    const adjustColumns = ({ columns }) => {
        return [
            {
                name: 'name',
                label: 'Name',
                render: ({ row }) => {
                    return pawnInfo[row['pawn.pawnId']]?.name;
                },
            },
            {
                name: 'email',
                label: 'Email',
                render: ({ row }) => {
                    return pawnInfo[row['pawn.pawnId']]?.email;
                },
            },
            ...columns.filter(
                column =>
                    ![
                        'pawn.pawnId',
                        'important.ancestorRoles.learner.fields.studentId',
                        'important.ancestorRoles.learner.ancestorRoles.instructor.user.username',
                    ].includes(column.name)
            ),
        ];
    };

    return (
        <ProgressWithProjectId
            hideProjectDropdown
            hideDeploymentDropdown
            labelForQuest="Course"
            showByWhitelist={['assignment']}
            pawnIdWhitelist={pawnIds}
            adjustColumns={adjustColumns}
            roleHandles={roleHandles}
        />
    );
};

export const instructors = ({ instructorList, context }) => {
    const getContent = () => {
        const table = (
            <div style={{ textAlign: 'center' }}>
                <Table
                    data={instructorList}
                    columns={[
                        { name: 'name', label: 'Name', sort: true, filter: true },
                        { name: 'email', label: 'Email', sort: true, filter: true },
                        {
                            name: 'school',
                            label: 'School',
                            sort: true,
                            filter: true,
                            render: value => value || '',
                        },
                        {
                            name: 'supervisor',
                            label: 'Supervisor',
                            sort: true,
                            filter: true,
                            render: value => value || '',
                        },
                        {
                            name: 'createdAssignmentPlanList',
                            label: 'Assessment Plans',
                            sort: true,
                            render: v =>
                                assessmentPlans({
                                    planList: v,
                                    context: v[0]?.createdByPawn?.user?.fullNameWithUsername || '',
                                    row: instructorList,
                                    adjustColumns: c => {
                                        return c.filter(col => col.label !== 'Actions');
                                    },
                                }),
                            renderCSV: value => (value ? value.length : 0),
                        },
                        {
                            name: 'childRoles',
                            label: 'Students',
                            sort: true,
                            render: v => {
                                return v && v.learnerList.length;
                            },
                            sortSettings: {
                                custom: row => {
                                    return row.childRoles.learnerList.length;
                                },
                            },
                        },
                    ]}
                    isCsvDownload
                />
            </div>
        );

        const tabList = [
            { name: 'list', title: 'Providers', content: table },
            { name: 'progress', title: 'PD Progress', content: renderCourseProgress(instructorList) },
        ];

        return <Tabs tabList={tabList} />;
    };

    return (
        <div>
            {instructorList.length > 0 ? (
                <Tooltip title={`Providers (${context})`}>
                    <Modal
                        trigger={
                            <a href="#">
                                <IconModal /> {instructorList.length}
                            </a>
                        }
                        title={`Providers (${context})`}
                        size="99vw"
                        render={getContent}
                    />
                </Tooltip>
            ) : (
                0
            )}
        </div>
    );
};

export const responseFlags = ({ row, learnerList, context }) => {
    const allFlags = learnerList.flatMap(learner => learner.assignmentProgressList).flatMap(ap => ap.flagProgressList);
    const unresolvedFlags = allFlags.filter(flag => flag.status === 'flagged');

    return (
        <div>
            {allFlags.length > 0 ? (
                <Tooltip title={`Flags (${context})`}>
                    <Modal
                        trigger={
                            <a href="#">
                                <IconModal /> {allFlags.length}{' '}
                                {unresolvedFlags.length > 0 && (
                                    <div>
                                        <strong className="badge bg-danger">{unresolvedFlags.length} unresolved</strong>
                                    </div>
                                )}
                            </a>
                        }
                        title={`Flags (${context})`}
                        size="large"
                    >
                        <div style={{ textAlign: 'center' }}>
                            <Table
                                rowKey="flagProgressId"
                                data={allFlags}
                                columns={[
                                    {
                                        name: 'flag',
                                        label: 'Flag Name',
                                        sort: true,
                                        filter: true,
                                        render: flag => flag.name,
                                        filterSettings: {
                                            type: 'text',
                                            custom: (r, value) => {
                                                return r.flag.name.toLowerCase().includes(value.toLowerCase());
                                            },
                                        },
                                    },
                                    { name: 'flaggedDate', label: 'Date Flagged', sort: true, filter: true },
                                    {
                                        name: 'status',
                                        label: 'Status',
                                        sort: true,
                                        render: v => {
                                            return (
                                                <span
                                                    className={`badge ${
                                                        v === 'unflagged' ? 'text-bg-primary' : 'text-bg-danger'
                                                    }`}
                                                >
                                                    {v === 'unflagged' ? 'Resolved' : 'Unresolved'}
                                                </span>
                                            );
                                        },
                                    },
                                    {
                                        name: 'assignmentProgress',
                                        label: 'Actions',
                                        render: v => {
                                            return (
                                                <div>
                                                    <Link
                                                        to={{
                                                            pathname: '/flags',
                                                            state: {
                                                                provider:
                                                                    v?.pawn?.parentRoles?.instructor?.pawn?.pawnId ||
                                                                    row['pawn.pawnId'],
                                                                groupingUnit: row['group.groupId'],
                                                                student: row ? row.id : '',
                                                            },
                                                        }}
                                                    >
                                                        <IconArrow /> View on Flags Page
                                                    </Link>
                                                </div>
                                            );
                                        },
                                    },
                                ]}
                                isCsvDownload
                                csvOmittedColumns={['assignmentProgress']}
                            />
                        </div>
                    </Modal>
                </Tooltip>
            ) : (
                0
            )}
        </div>
    );
};

export const sites = ({ siteList, context }) => {
    return (
        <div>
            {siteList.length > 0 ? (
                <Tooltip title={`Schools in ${context}`}>
                    <Modal
                        trigger={
                            <a href="#">
                                <IconModal /> {siteList.length}
                            </a>
                        }
                        title={`Schools in ${context}`}
                        size="large"
                    >
                        <Table
                            data={siteList}
                            columns={[
                                {
                                    name: 'schoolId',
                                    label: 'School ID',
                                    sort: true,
                                    filter: true,
                                    render: value => value || '',
                                },
                                { name: 'label', label: 'School', sort: true, filter: true },
                                { name: 'learnerCount', label: 'Students', sort: true },
                            ]}
                            isCsvDownload
                        />
                    </Modal>
                </Tooltip>
            ) : (
                0
            )}
        </div>
    );
};

export const supervisors = ({ supervisorList, context }) => {
    if (supervisorList.length === 0) {
        return null;
    }

    const getContent = () => {
        const table = (
            <div style={{ textAlign: 'center' }}>
                <Table
                    data={supervisorList}
                    columns={[
                        { name: 'name', label: 'Name', sort: true, filter: true },
                        { name: 'email', label: 'Email', sort: true, filter: true },
                        {
                            name: 'childRoles',
                            label: 'Providers',
                            sort: true,
                            filter: true,
                            render: v => {
                                const instructorList = v.instructorList;
                                return instructors({
                                    instructorList,
                                    context: instructorList[0]?.supervisor,
                                });
                            },
                            sortSettings: {
                                custom: row => {
                                    return row.childRoles.instructorList.length;
                                },
                            },
                        },
                    ]}
                />
            </div>
        );

        const tabList = [
            { name: 'list', title: 'Providers', content: table },
            {
                name: 'progress',
                title: 'PD Progress',
                content: renderCourseProgress(supervisorList, ['supervisor']),
            },
        ];

        return <Tabs tabList={tabList} />;
    };

    return (
        <Tooltip title={`Supervisors in ${context}`}>
            <Modal
                trigger={
                    <a href="#">
                        <IconModal /> {supervisorList.length}
                    </a>
                }
                title={`Supervisors in ${context}`}
                size="large"
                render={getContent}
            />
        </Tooltip>
    );
};
